import { SectionCard } from '@atlas/ui-components'
import { Badge, type BadgeProps, ProgressBar } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'

export interface ProcessStat {
	label: string
	value: number | string
}

export interface ProcessCardProps {
	step: number
	title: string
	description: string
	status: { label: string; variant: BadgeProps['variant'] }
	/** Done and total; omitted for a process with no measurable progress. */
	progress?: { done: number; total: number; noun: string }
	stats: ProcessStat[]
	/** Details below the numbers: failures, blocked rows. */
	children?: ReactNode
}

/** One step of the asset sheet pipeline: its state, how far it got, and its numbers. */
export function ProcessCard({ step, title, description, status, progress, stats, children }: ProcessCardProps) {
	return (
		<SectionCard
			aria-label={title}
			title={`${step}. ${title}`}
			description={description}
			action={
				<Badge variant={status.variant} size="sm">
					{status.label}
				</Badge>
			}
		>
			<div className="flex flex-col gap-ds-4">
				{progress && (
					<div className="flex flex-col gap-ds-1">
						<ProgressBar
							aria-label={`${title} progress`}
							value={progress.done}
							maxValue={Math.max(progress.total, 1)}
						/>
						<p className="text-ds-xs text-ds-text-muted">
							{progress.done} of {progress.total} {progress.noun}
						</p>
					</div>
				)}

				<dl className="grid grid-cols-2 gap-ds-3 sm:grid-cols-4">
					{stats.map((s) => (
						<div key={s.label} className="flex flex-col gap-ds-1">
							<dt className="text-ds-xs text-ds-text-muted">{s.label}</dt>
							<dd className="text-ds-base text-ds-text">{s.value}</dd>
						</div>
					))}
				</dl>

				{children}
			</div>
		</SectionCard>
	)
}
